import { AlertTriangle, BookOpen, ExternalLink, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Loader from '@/components/Loader';
import {
	useCourseCount,
	useCourseStatusStats,
	useDisciplineStats,
	useSubmissionStats,
	useUnassignedCourses,
	useUnassignedUsers,
	useUserCount,
	useUserRoleStats,
} from '@/hooks/queries/useStats';
import { StyledJumboText, StyledSubHeader } from '@/Shared/Typography/typography';

const ROLE_COLORS: Record<string, string> = {
	admin: '#7c3aed',
	dept: '#2563eb',
	user: '#059669',
};

const getRoleLabel = (role: string | null): string => {
	if (role === 'admin') return 'Admin';
	if (role === 'dept') return 'Dept Chair';
	return 'Teacher';
};

const ProgressBar = ({ percentage, color }: { percentage: number; color?: string }) => (
	<div className="mt-2 w-full h-[5px] rounded-full overflow-hidden bg-[rgba(0,15,25,0.08)]">
		<div
			className="h-full rounded-full transition-[width] duration-400"
			style={{
				width: `${Math.min(percentage, 100)}%`,
				backgroundColor: color ?? 'var(--primary-color)',
			}}
		/>
	</div>
);

const LegendDot = ({ color, children }: { color: string; children: React.ReactNode }) => (
	<div className="relative ml-4">
		<span
			className="absolute -left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full opacity-80"
			style={{ backgroundColor: color }}
		/>
		{children}
	</div>
);

const Stats = () => {
	const { data: courseCount, isLoading: isLoadingCourseCount } = useCourseCount();
	const { data: unassignedCourses, isLoading: isLoadingUnassignedCourses } = useUnassignedCourses();
	const { data: userCount, isLoading: isLoadingUserCount } = useUserCount();
	const { data: unassignedUsers, isLoading: isLoadingUnassignedUsers } = useUnassignedUsers();
	const { data: submissionStats, isLoading: isLoadingSubmissions } = useSubmissionStats();
	const { data: disciplineStats, isLoading: isLoadingDisciplines } = useDisciplineStats();
	const { data: courseStatusStats, isLoading: isLoadingCourseStatus } = useCourseStatusStats();
	const { data: userRoleStats, isLoading: isLoadingUserRoles } = useUserRoleStats();

	const isLoading =
		isLoadingCourseCount ||
		isLoadingUnassignedCourses ||
		isLoadingUserCount ||
		isLoadingUnassignedUsers ||
		isLoadingSubmissions ||
		isLoadingDisciplines ||
		isLoadingCourseStatus ||
		isLoadingUserRoles;

	if (isLoading) return <Loader />;

	const totalCourses = courseCount?.count ?? 0;
	const unmappedCount = courseCount?.unusedCourses?.length ?? 0;
	const mappedCount = totalCourses - unmappedCount;
	const unassignedCourseCount = unassignedCourses?.count ?? 0;
	const totalUsers = userCount?.count ?? 0;
	const unassignedUserCount = unassignedUsers?.users.length ?? 0;

	const unassignedCoursePercent =
		totalCourses > 0 ? (unassignedCourseCount / totalCourses) * 100 : 0;
	const mappedPercent = totalCourses > 0 ? (mappedCount / totalCourses) * 100 : 0;
	const assignedUserPercent =
		totalUsers > 0 ? ((totalUsers - unassignedUserCount) / totalUsers) * 100 : 100;

	const cardClasses =
		'bg-(--card-bg-color) rounded-[6px] shadow-[0px_2px_4px_1px_rgba(0,15,25,0.05),0px_0px_4px_0_rgba(0,15,25,0.05)] p-(--spacing-4) w-full h-full [&_small]:text-(--header-text) [&_small]:font-(--font-regular) [&_small]:italic [&_small]:text-[1.1rem]';

	const gridClasses = 'grid gap-8 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]';

	return (
		<div className="flex flex-col gap-6 h-full overflow-y-auto pb-6">
			{/* Row 1: Key metrics */}
			<div className={gridClasses}>
				<div className={cardClasses}>
					<div className="flex justify-between items-start">
						<StyledSubHeader className="font-bold" fontSize="16px">
							Unassigned Courses
						</StyledSubHeader>
						<AlertTriangle size={18} className="text-amber-500 shrink-0" />
					</div>
					<StyledJumboText fontSize="80px" mb="4px">
						{unassignedCourseCount}
					</StyledJumboText>
					<small>
						{unassignedCoursePercent.toFixed(1)}% of {totalCourses} total courses
					</small>
					<ProgressBar percentage={unassignedCoursePercent} color="#f59e0b" />
				</div>

				<div className={cardClasses}>
					<div className="flex justify-between items-start">
						<StyledSubHeader className="font-bold" fontSize="16px">
							Total Users
						</StyledSubHeader>
						<Users size={18} className="shrink-0 opacity-40" />
					</div>
					<StyledJumboText fontSize="80px" mb="4px">
						{totalUsers}
					</StyledJumboText>
					<small>
						{userRoleStats?.teachers} teachers · {userRoleStats?.deptChairs} dept chairs ·{' '}
						{userRoleStats?.admins} admins
					</small>
					<ProgressBar percentage={assignedUserPercent} />
				</div>

				<div className={cardClasses}>
					<div className="flex justify-between items-start">
						<StyledSubHeader className="font-bold" fontSize="16px">
							Total Courses
						</StyledSubHeader>
						<BookOpen size={18} className="shrink-0 opacity-40" />
					</div>
					<StyledJumboText fontSize="80px" mb="4px">
						{totalCourses}
					</StyledJumboText>
					<small>
						{mappedCount} on map · {unmappedCount} unmapped
					</small>
					<ProgressBar percentage={mappedPercent} color="#22c55e" />
				</div>
			</div>

			{/* Row 2: Detailed breakdowns */}
			<div className={gridClasses}>
				<div className={cardClasses}>
					<StyledSubHeader className="font-bold" fontSize="16px" mb="16px">
						Course Status
					</StyledSubHeader>
					<div className="flex gap-2">
						<div className="flex-1 text-center">
							<StyledJumboText fontSize="56px" mb="0" className="text-center">
								{courseStatusStats?.ap}
							</StyledJumboText>
							<small className="block text-center">AP Courses</small>
						</div>
						<div className="flex-1 text-center">
							<StyledJumboText fontSize="56px" mb="0" className="text-center">
								{courseStatusStats?.draft}
							</StyledJumboText>
							<small className="block text-center">In Draft</small>
						</div>
						<div className="flex-1 text-center">
							<StyledJumboText fontSize="56px" mb="0" className="text-center">
								{courseStatusStats?.archived}
							</StyledJumboText>
							<small className="block text-center">Archived</small>
						</div>
					</div>
				</div>

				<div className={cardClasses}>
					<StyledSubHeader className="font-bold" fontSize="16px" mb="16px">
						Submission Pipeline
					</StyledSubHeader>
					<div className="grid grid-cols-2 gap-4">
						{(
							[
								{ label: 'Approved', value: submissionStats?.approved, color: '#22c55e' },
								{ label: 'Pending Dept', value: submissionStats?.pendingDept, color: '#f59e0b' },
								{ label: 'Pending Admin', value: submissionStats?.pendingAdmin, color: '#3b82f6' },
								{ label: 'Rejected', value: submissionStats?.rejected, color: '#ef4444' },
							] as const
						).map((item) => (
							<div
								key={item.label}
								className="flex-1 pt-2"
								style={{ borderTop: `3px solid ${item.color}` }}
							>
								<StyledJumboText fontSize="40px" mb="2px">
									{item.value}
								</StyledJumboText>
								<small>{item.label}</small>
							</div>
						))}
					</div>
				</div>

				<div className={cardClasses}>
					<StyledSubHeader className="font-bold" fontSize="16px" mb="8px">
						Courses by Discipline
					</StyledSubHeader>
					<div>
						{disciplineStats
							?.filter((d) => d.count > 0)
							.map((d) => (
								<div key={d.id} className="flex items-center justify-between mb-1.5">
									<LegendDot color={d.color}>{d.name}</LegendDot>
									<span className="text-(--header-text) shrink-0 text-[1.4rem] font-bold ml-2">
										{d.count}
									</span>
								</div>
							))}
					</div>
				</div>
			</div>

			{/* Row 3: Lists */}
			<div className={gridClasses}>
				<div className={cardClasses}>
					<StyledSubHeader className="font-bold" fontSize="16px" mb="8px">
						Unmapped Courses
					</StyledSubHeader>
					<small>Courses not represented within the course map.</small>
					<div className="flex flex-col gap-1 mt-3">
						{(courseCount?.unusedCourses ?? []).map((course) => (
							<div
								key={course.course_id}
								className="flex items-stretch bg-[rgba(0,15,25,0.04)] rounded-[6px] overflow-hidden"
							>
								<span className="flex-1 text-(--header-text) text-[1.3rem] py-2 px-3">
									{course.course_name}
								</span>
								<Link
									to={`/dashboard/courses/${course.course_id}`}
									title={`Go to ${course.course_name}`}
									className="flex items-center justify-center shrink-0 bg-(--header-text) text-(--card-bg-color) px-3"
								>
									<ExternalLink size={14} />
								</Link>
							</div>
						))}
					</div>
				</div>

				<div className={cardClasses}>
					<StyledSubHeader className="font-bold" fontSize="16px">
						Unassigned Users
					</StyledSubHeader>
					<div className="flex flex-col gap-4 mt-3">
						{(['user', 'dept', 'admin'] as const).map((role) => {
							const group =
								unassignedUsers?.users.filter((u) => (u.user_role ?? 'user') === role) ?? [];
							if (group.length === 0) return null;
							return (
								<div key={role}>
									<div className="flex items-center gap-2 mb-2">
										<span
											className="text-[1.1rem] font-bold uppercase tracking-wide"
											style={{ color: ROLE_COLORS[role] }}
										>
											{getRoleLabel(role)}
										</span>
										<span
											className="text-[1.1rem] rounded-full px-1.5 py-0.5 font-bold"
											style={{
												backgroundColor: `${ROLE_COLORS[role]}20`,
												color: ROLE_COLORS[role],
											}}
										>
											{group.length}
										</span>
									</div>
									<div className="grid grid-cols-2 gap-1">
										{group.map((user) =>
											user.user_first || user.user_last ? (
												<span
													key={user.user_id}
													className="text-(--header-text) text-[1.3rem] bg-[rgba(0,15,25,0.04)] rounded-[6px] py-1.5 px-3 truncate"
												>
													{user.user_first} {user.user_last}
												</span>
											) : null
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Stats;
